import { forwardRef, SyntheticEvent, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tooltip } from 'primereact/tooltip';
import { Button } from 'primereact/button';
import { useUnitFocus } from '../../custom-hooks/useUnitFocus';
import { useUnitFocusEligibility } from '../../custom-hooks/useFocusEligibility';
import {
  useEligibleFocusClasses,
  useRoadmapMembership,
  useStudentsCurrentUnits,
} from '../../custom-hooks/useFocusClassData';
import { useAppSelector } from '../../redux/hooks';
import { BlockOtherUnitsSwitch } from './BlockOtherUnitsSwitch';
import { ClassSelectStep } from './ClassSelectStep';
import { AlreadyFinishedModal, FinishedStudentRow } from './AlreadyFinishedModal';
import {
  classIdsWithFocusRecipients,
  ProgressStudent,
  resolveFocusRestrictDefault,
} from '../../businessLogicService/focusService';
import { buildFocusActivatedMessage } from './focusMessages';
import { selectFocuses, selectActiveFocusClassIds } from '../../redux/reducers/focusSlice';
import { ExclusionReason } from '../../models/UnitFocus';
import { Eye } from 'lucide-react';
import { FocusReachSummary } from './FocusReachSummary';
import { FocusSuccessToast, FocusSuccessToastHandle } from './FocusSuccessToast';
import { useUserActionMutation } from '../../custom-hooks/useUserActionMutation';
import { selectUser } from '../../redux/reducers/userSlice';
import { useUnitFocusEvent } from '../../custom-hooks/useUnitFocusEvent';

interface Props {
  targetId: string;
  classId?: string;
  className?: string;
  roadmapId: string;
  renderTrigger?: boolean;
  /** Called once a focus is successfully activated — auto-completes the onboarding task. */
  onFocusActivated?: () => void;
}

export interface ActivateFocusPopoverHandle {
  toggle: (e: SyntheticEvent) => void;
}

const ACTIVATE_BTN =
  '!tw-whitespace-nowrap !tw-rounded-full !tw-border-indigo-600 !tw-bg-indigo-600 !tw-px-6 !tw-py-3 !tw-font-bold';
const CONFIRM_BTN =
  '!tw-w-full !tw-justify-center !tw-rounded-full !tw-border-indigo-600 !tw-bg-indigo-600 !tw-px-6 !tw-py-3 !tw-font-bold';
const LINK =
  'tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-border-none tw-bg-transparent tw-p-0 tw-font-atkinson tw-font-bold tw-text-indigo-600';

interface FocusReach {
  reached: number;
  total: number;
  finished: number;
}

function summarizeFocusReach(
  included: ProgressStudent[],
  excluded: { student: ProgressStudent; reason: ExclusionReason }[],
): FocusReach {
  return {
    reached: included.length,
    total: included.length + excluded.length,
    finished: excluded.filter(e => e.reason === 'finished_this_unit').length,
  };
}

export const ActivateFocusPopover = forwardRef<ActivateFocusPopoverHandle, Props>(function ActivateFocusPopover(
  { targetId, classId = '', className = '', roadmapId, renderTrigger = true, onFocusActivated },
  ref,
) {
  const userRedux = useAppSelector(selectUser);
  const op = useRef<OverlayPanel>(null);
  const focusToast = useRef<FocusSuccessToastHandle>(null);
  useImperativeHandle(ref, () => ({ toggle: (e: SyntheticEvent) => op.current?.toggle(e) }), []);
  const { activate } = useUnitFocus();

  const [open, setOpen] = useState(false);
  const focuses = useAppSelector(selectFocuses);
  const persistedRestrict = resolveFocusRestrictDefault(focuses, targetId);
  const [restrict, setRestrict] = useState(persistedRestrict);
  const [step, setStep] = useState<'confirm' | 'select'>('confirm');
  const activeFocusClassIds = useAppSelector(selectActiveFocusClassIds)[targetId];
  const seedClassIds = () => {
    const seed = activeFocusClassIds?.length ? [...activeFocusClassIds] : classId ? [classId] : [];
    return classId && !seed.includes(classId) ? [classId, ...seed] : seed;
  };
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>(seedClassIds);
  const [submitting, setSubmitting] = useState(false);
  const [confirmError, setConfirmError] = useState(false);
  const [showFinished, setShowFinished] = useState(false);
  const captureUnitFocusEvent = useUnitFocusEvent();
  const blockingChoiceRef = useRef<'left_default' | 'chose_off' | 'chose_on'>('left_default');
  const membership = useRoadmapMembership(open, selectedClassIds, roadmapId);
  const memberFilter: ReadonlySet<string> | null = membership.loading || membership.error ? null : membership.memberIds;
  const elig = useUnitFocusEligibility(open, targetId, selectedClassIds, memberFilter);
  const loadingReach = membership.loading || elig.loading;
  const { reached, total, finished } = summarizeFocusReach(elig.included, elig.excluded);
  const [commitMutation] = useUserActionMutation();

  const { classes: teacherClasses, loading: classesLoading } = useEligibleFocusClasses(open, roadmapId);
  const labelById = useMemo(() => new Map(teacherClasses.map(c => [c.id, c.label])), [teacherClasses]);

  useEffect(() => {
    if (!open || classesLoading) return;
    const eligible = new Set(teacherClasses.map(c => c.id));
    setSelectedClassIds(prev => prev.filter(id => id === classId || eligible.has(id)));
  }, [open, classesLoading, teacherClasses, classId]);
  const labelFor = (id: string): string => (id === classId ? className : labelById.get(id) ?? id);

  const selectedCount = selectedClassIds.length;
  const isMulti = selectedCount > 1;
  const headerChipLabel =
    selectedCount === 0 ? 'Select classes' : isMulti ? `${selectedCount} classes` : labelFor(selectedClassIds[0]);
  const selectedLabelsTitle = isMulti ? selectedClassIds.map(labelFor).join(', ') : '';

  const countTooltipClass = `nc-focus-classes-${useId().replace(/:/g, '')}`;

  const finishedStudents = elig.excluded.filter(e => e.reason === 'finished_this_unit');
  const finishedStudentIds = finishedStudents.map(e => e.student.id);
  const { unitsByStudent } = useStudentsCurrentUnits(open && showFinished, finishedStudentIds);

  const finishedRows: FinishedStudentRow[] = finishedStudents.map(e => {
    const unit = unitsByStudent.get(e.student.id);
    return {
      id: e.student.id,
      name: e.student.name,
      currentUnitLabel: unit ? `Unit ${unit.name}` : '—',
      publicUserId: e.student.publicUserId,
    };
  });

  // Only a deliberate flip counts as a choice — an untouched switch stays 'left_default'.
  const handleRestrictChange = (next: boolean): void => {
    setRestrict(next);
    blockingChoiceRef.current = next ? 'chose_on' : 'chose_off';
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setConfirmError(false);
    try {
      await activate({
        targetId,
        userIds: elig.included.map(s => s.id),
        classIds: classIdsWithFocusRecipients(
          selectedClassIds,
          membership.memberIdsByClass,
          elig.included.map(s => s.id),
        ),
        focusMode: restrict ? 'restrict' : 'recommend',
      });
      const { summary, detail } = buildFocusActivatedMessage(elig.unitName, reached);
      focusToast.current?.show(summary, detail);
      op.current?.hide();
      // Record completion only when the focus actually activated — this drives the onboarding
      // task's auto-complete, so it must not fire on the error path.
      commitMutation({
        variables: {
          input: {
            userAction: {
              userId: `UserNode:${userRedux?.id}`,
              actionTypeId: 'ActionTypeNode:20',
              timeStamp: new Date().toISOString(),
              description: 'Set Class Focus Completed',
            },
          },
        },
      });
      onFocusActivated?.();
    } catch {
      setConfirmError(true);
    } finally {
      setSubmitting(false);
    }

    captureUnitFocusEvent(
      'Unit_focus_created',
      {
        classIds: selectedClassIds,
        targetId,
        targetGroupId: roadmapId,
        blockingEnabled: restrict,
      },
      {
        blocking_choice: blockingChoiceRef.current,
        class_count: selectedClassIds.length,
      },
    );
  };

  return (
    <>
      {renderTrigger && (
        <Button
          label="Activate focus"
          icon="pi pi-bullseye"
          onClick={e => op.current?.toggle(e)}
          className={ACTIVATE_BTN}
        />
      )}

      <OverlayPanel
        ref={op}
        onShow={() => {
          setOpen(true);
          setRestrict(persistedRestrict);
          blockingChoiceRef.current = 'left_default';
          setSelectedClassIds(seedClassIds());
        }}
        onHide={() => {
          setOpen(false);
          setStep('confirm');
          setConfirmError(false);
          setShowFinished(false);
        }}
        className="!tw-w-96 !tw-rounded-xl !tw-border-2 !tw-border-neutral-200 !tw-shadow-[0px_0px_15px_rgba(132,123,111,0.2)]"
        pt={{ content: { className: 'p-0' } }}
      >
        <div className="tw-max-h-[80vh] tw-overflow-y-auto">
          {step === 'select' ? (
            <ClassSelectStep
              currentClassId={classId}
              currentClassLabel={className}
              options={teacherClasses}
              loading={classesLoading}
              selectedIds={selectedClassIds}
              onChange={setSelectedClassIds}
              onBack={() => setStep('confirm')}
              onDone={() => setStep('confirm')}
              reached={reached}
              total={total}
              finished={finished}
            />
          ) : (
            <div className="tw-flex tw-flex-col">
              <div className="tw-flex tw-flex-col tw-gap-2 tw-border-solid tw-border-b-2 tw-border-neutral-100 tw-px-4 tw-py-3">
                <span className="tw-font-atkinson tw-text-xs tw-font-bold tw-uppercase tw-text-neutral-600">
                  You&rsquo;re about to
                </span>
                <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-font-atkinson tw-text-base tw-text-neutral-700">
                  Focus
                  <span
                    className={`${
                      isMulti ? `${countTooltipClass} ` : ''
                    }tw-rounded-lg tw-bg-neutral-100 tw-px-2 tw-py-1 tw-font-bold tw-leading-5 tw-text-neutral-950 ${
                      isMulti ? 'tw-cursor-help' : 'tw-cursor-default'
                    }`}
                    aria-label={isMulti ? `Classes: ${selectedLabelsTitle}` : undefined}
                  >
                    {headerChipLabel}
                  </span>
                  on <span className="tw-font-bold tw-text-neutral-950">Unit {elig.unitName || '—'}</span>
                </span>

                {isMulti && (
                  <Tooltip
                    target={`.${countTooltipClass}`}
                    mouseTrack
                    mouseTrackTop={12}
                    position="bottom"
                    className="tw-font-atkinson !tw-max-w-[280px] !tw-rounded-lg"
                  >
                    <div className="tw-flex tw-flex-col tw-gap-0.5 tw-p-0.5 tw-text-left">
                      {selectedClassIds.map(id => (
                        <span key={id} className="tw-text-xs tw-leading-[18px]">
                          {labelFor(id)}
                        </span>
                      ))}
                    </div>
                  </Tooltip>
                )}
              </div>

              <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <span className="tw-pl-3 tw-font-atkinson tw-text-sm tw-font-bold tw-text-neutral-700">
                    Classes <span className="tw-text-[#b82828]">*</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setStep('select')}
                    aria-label="Edit selected classes"
                    className="tw-flex tw-h-11 tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-3 tw-overflow-hidden tw-rounded-xl tw-border-solid tw-border-2 tw-border-neutral-100 tw-bg-neutral-0 tw-px-3 tw-py-0"
                  >
                    <span className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-1">
                      <span
                        className={`tw-truncate tw-font-atkinson tw-text-base tw-leading-5 ${
                          selectedCount === 0
                            ? 'tw-font-normal tw-text-neutral-700'
                            : 'tw-font-bold tw-text-neutral-950'
                        }`}
                      >
                        {selectedCount === 0 ? 'Select classes' : labelFor(selectedClassIds[0])}
                      </span>
                      {isMulti && (
                        <span className="tw-inline-flex tw-h-[18px] tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-solid tw-border-stone-200 tw-bg-stone-50 tw-px-1 tw-py-0.5 tw-font-atkinson tw-text-xs tw-font-bold tw-text-stone-800">
                          <i className="pi pi-plus tw-text-[10px]" aria-hidden />
                          {selectedCount - 1} more
                        </span>
                      )}
                    </span>
                    <span className={`${LINK} tw-shrink-0 tw-text-xs`}>Add classes</span>
                  </button>
                </div>

                <BlockOtherUnitsSwitch
                  checked={restrict}
                  onChange={handleRestrictChange}
                  title="Other units blocked"
                  description="Students can only work in the focused Unit"
                  showDescription={!isMulti}
                  showSectionLabel={false}
                />

                <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded-xl tw-border-solid tw-border-2 tw-border-neutral-100 tw-bg-neutral-50 tw-px-4 tw-py-3">
                  {membership.error ? (
                    // Roster load failed: the reach count would be misleading (it can only
                    // reflect the classes that succeeded), so replace it with an error and
                    // block Confirm below rather than silently focusing a partial set.
                    <span role="alert" className="tw-font-atkinson tw-text-sm tw-text-[#b82828]">
                      Couldn&rsquo;t load class rosters, so we can&rsquo;t tell who gets this focus. Please try again.
                    </span>
                  ) : (
                    <>
                      <span className="tw-font-atkinson tw-text-sm tw-text-neutral-950">
                        <FocusReachSummary reached={reached} total={total} loading={loadingReach} />
                      </span>
                      <div className="tw-h-0.5 tw-w-full tw-rounded-[80px] tw-bg-neutral-100" />
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <span className="tw-size-2 tw-shrink-0 tw-rounded-full tw-bg-[#007a54]" aria-hidden />
                        <span className="tw-flex-1 tw-font-atkinson tw-text-sm tw-text-neutral-950">
                          <strong>{finished}</strong> finished already
                        </span>
                        {finished > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowFinished(true)}
                            className={`${LINK} tw-text-sm`}
                            aria-label="View student list"
                          >
                            <Eye />
                            View
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="tw-border-solid tw-border-t-2 tw-border-neutral-100 tw-px-4 tw-py-3">
                <Button
                  label="Confirm focus"
                  icon="pi pi-check"
                  iconPos="right"
                  onClick={handleConfirm}
                  loading={submitting}
                  disabled={
                    selectedCount === 0 || loadingReach || elig.included.length === 0 || submitting || membership.error
                  }
                  className={CONFIRM_BTN}
                />
                {confirmError && (
                  <p role="alert" className="tw-mx-0 tw-mb-0 tw-mt-2 tw-font-atkinson tw-text-sm tw-text-[#b82828]">
                    Could not set focus. Please try again.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <AlreadyFinishedModal
          visible={showFinished}
          onHide={() => setShowFinished(false)}
          students={finishedRows}
          total={total}
        />
      </OverlayPanel>

      <FocusSuccessToast ref={focusToast} />
    </>
  );
});
